// Translates raw mouse/keyboard events into FrameInput. `fire` and `start` are
// EDGE events: read() returns them once and clears them, so a single click can't
// fire twice when one animation frame runs multiple fixed sub-steps.

import type { FireCommand, FrameInput, Vec2 } from '../core';
import { FIELD_W, FIELD_H } from '../core';
import { screenToField, type Viewport } from './canvas';

export interface InputController {
  read: () => FrameInput;
  pointer: () => Vec2;
  dispose: () => void;
}

export function createInput(
  canvas: HTMLCanvasElement,
  viewport: () => Viewport,
): InputController {
  let pointer: Vec2 = { x: FIELD_W / 2, y: FIELD_H / 2 };
  let pendingFire: FireCommand | null = null;
  let pendingStart = false;

  const onMove = (e: MouseEvent): void => {
    pointer = screenToField(e.clientX, e.clientY, viewport());
  };
  const onDown = (e: MouseEvent): void => {
    pointer = screenToField(e.clientX, e.clientY, viewport());
    pendingFire = { target: pointer };
    pendingStart = true; // a click also starts/continues the game
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === ' ' || e.key === 'Enter') pendingStart = true;
  };

  canvas.addEventListener('mousemove', onMove);
  canvas.addEventListener('mousedown', onDown);
  window.addEventListener('keydown', onKey);

  return {
    read: (): FrameInput => {
      const input: FrameInput = { fire: pendingFire, start: pendingStart, pointer };
      pendingFire = null;
      pendingStart = false;
      return input;
    },
    pointer: () => pointer,
    dispose: (): void => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    },
  };
}
